/**
 * NavigatorInsight — "What the Navigator sees".
 *
 * A personalized observation on Today, drawn from the user's OWN data — the
 * Navigator's sentence-form cross-thread insights first, then a recurring (or
 * earned positive) behavioral pattern. Reinforces the persistent-memory moat
 * with the Navigator's current read of you — not a generic quote.
 *
 * Pure read (cross-thread insights + pattern_log, both already in /state).
 * Renders NOTHING when there's no real observation — never generic filler.
 * Tone follows drive_level (softer framing + amber-not-red at Coach).
 */

import { Eye } from 'lucide-react';

const PATTERN_LABELS = {
  strong_follow_through: 'Strong Follow-Through',
  progress_momentum: 'Progress Momentum',
  early_delivery: 'Early Delivery',
  last_minute_delivery: 'Last-Minute Delivery',
  no_proactive_communication: 'No Proactive Communication',
  inward_focus: 'Inward Focus',
  avoidance: 'Avoidance',
  incomplete_submission: 'Incomplete Submission',
  over_commitment: 'Over-Commitment',
  self_sabotage: 'Self-Sabotage',
  external_blame: 'External Blame',
  isolation: 'Isolation',
  perfectionism: 'Perfectionism',
};

const POSITIVE = new Set(['strong_follow_through', 'progress_momentum', 'early_delivery']);

const POSITIVE_OBS = {
  strong_follow_through: 'You keep doing what you said you would. That consistency is becoming your signature.',
  progress_momentum: "You're stringing wins together — the momentum is real, not luck.",
  early_delivery: 'You keep delivering ahead of the line. Most people never get there.',
};

const NEGATIVE_OBS = {
  last_minute_delivery: "You keep finishing at the buzzer — the work lands, but it's quietly costing you.",
  no_proactive_communication: 'You tend to surface only once things are already tense. Reaching out earlier changes the whole conversation.',
  inward_focus: 'A lot of your energy is turning inward lately. The fastest shift usually comes from one outward action.',
  avoidance: "There's something you keep circling instead of touching — and it isn't getting smaller.",
  incomplete_submission: 'Your work keeps going out a step before done. The last 10% is where it actually lands.',
  over_commitment: "You keep saying yes past your capacity. One protected 'no' buys back the rest.",
  self_sabotage: 'You tend to pull back right as things start working. Worth catching the moment it happens.',
  external_blame: "A lot is landing as outside your control. The part that isn't is where your leverage lives.",
  isolation: "You've been carrying this mostly alone. A single ask would lighten it.",
  perfectionism: 'You keep polishing past the point of return. Shipped beats perfect.',
};

const INSIGHT_META = {
  capacity_warning: { label: 'Capacity', tone: 'amber' },
  progress_correlation: { label: 'Progress link', tone: 'green' },
  avoidance_pattern: { label: 'Avoidance', tone: 'red' },
  momentum_building: { label: 'Momentum', tone: 'green' },
  thread_suggestion: { label: 'Suggestion', tone: 'slate' },
  cross_space_pattern: { label: 'Cross-space', tone: 'amber' },
};

const TONE_CHIP = {
  amber: 'bg-amber-500/15 text-amber-300 border-amber-700/40',
  green: 'bg-green-500/15 text-green-300 border-green-700/40',
  red: 'bg-red-500/15 text-red-300 border-red-700/40',
  slate: 'bg-slate-700/40 text-slate-300 border-slate-600/40',
};

function labelForPattern(type) {
  return PATTERN_LABELS[type] || type.replace(/_/g, ' ');
}

// Choose the single most salient observation: a real cross-thread insight wins;
// otherwise a recurring (3+) pattern, preferring an earned positive one. At Coach
// level the pattern fallback only surfaces positive observations — the harsher
// behavioral call-outs stay in the conversation, where tone is controlled.
function chooseObservation(insights, patterns, gentle) {
  const insight = (Array.isArray(insights) ? insights : [])
    .find(i => i && typeof i.insight_text === 'string' && i.insight_text.trim());
  if (insight) {
    const meta = INSIGHT_META[insight.insight_type]
      || { label: insight.insight_type ? insight.insight_type.replace(/_/g, ' ') : null, tone: 'slate' };
    return { body: insight.insight_text.trim(), label: meta.label, tone: meta.tone };
  }

  const escalated = (Array.isArray(patterns) ? patterns : []).filter(p => (p.recurrence_count || 0) >= 3);
  const pool = gentle ? escalated.filter(p => POSITIVE.has(p.pattern_type)) : escalated;
  const chosen = pool.find(p => POSITIVE.has(p.pattern_type)) || pool[0];
  if (chosen) {
    const positive = POSITIVE.has(chosen.pattern_type);
    const body = (positive ? POSITIVE_OBS : NEGATIVE_OBS)[chosen.pattern_type]
      || `${labelForPattern(chosen.pattern_type)} has come up ${chosen.recurrence_count}× recently.`;
    return { body, label: positive ? 'A strength' : 'A pattern', tone: positive ? 'green' : 'red' };
  }

  return null;
}

export default function NavigatorInsight({ insights, patterns, driveLevel = 'mentor', variant = 'default', currentRead = null, className = '' }) {
  const gentle = driveLevel === 'coach';
  const hero = variant === 'hero';
  const title = gentle ? 'A note from the Navigator' : 'What the Navigator sees';
  const boxClass = hero
    ? 'rounded-xl border border-blue-700/40 bg-slate-800/50 shadow-glow p-5'
    : 'rounded-lg border border-slate-700/50 bg-slate-800/40 shadow-soft p-4';

  // Gestalt mode — the synthesized standing read wins when one exists. Shows the
  // full cross-thread read with its single sharpest line set off beneath it.
  const readText = (currentRead?.text || '').trim();
  const readHighlight = (currentRead?.highlight || '').trim();
  if (readText) {
    return (
      <section aria-label="What the Navigator sees" className={`${boxClass} ${className}`}>
        <div className="flex items-center gap-2 mb-2">
          <Eye size={hero ? 16 : 14} className="text-blue-400 flex-shrink-0" />
          <h3 className="text-xs font-medium text-slate-400 uppercase tracking-wide">{title}</h3>
        </div>
        <p className={`text-slate-100 leading-snug ${hero ? 'text-base sm:text-lg' : 'text-sm'}`}>{readText}</p>
        {readHighlight && (
          <p className={`mt-3 pl-3 border-l-2 border-blue-500/50 text-slate-300 leading-snug ${hero ? 'text-sm' : 'text-xs'}`}>
            {readHighlight}
          </p>
        )}
      </section>
    );
  }

  // Fallback — the single most salient insight/pattern (pre-gestalt behavior),
  // shown when there's no standing read yet.
  const obs = chooseObservation(insights, patterns, gentle);
  if (!obs) return null;
  const tone = gentle && obs.tone === 'red' ? 'amber' : obs.tone;

  return (
    <section aria-label="What the Navigator sees" className={`${boxClass} ${className}`}>
      <div className="flex items-center gap-2 mb-2">
        <Eye size={hero ? 16 : 14} className="text-blue-400 flex-shrink-0" />
        <h3 className="text-xs font-medium text-slate-400 uppercase tracking-wide">{title}</h3>
        {obs.label && (
          <span className={`text-[11px] px-2 py-0.5 rounded-full border ml-auto flex-shrink-0 ${TONE_CHIP[tone] || TONE_CHIP.slate}`}>
            {obs.label}
          </span>
        )}
      </div>
      <p className={`text-slate-100 leading-snug ${hero ? 'text-base sm:text-lg' : 'text-sm text-slate-200'}`}>{obs.body}</p>
    </section>
  );
}
